/**
 * /fiscal/retenciones-lisr · Fase 18.4
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
    Tabs, TabsContent, TabsList, TabsTrigger,
} from "../../components/ui/tabs";
import { toast } from "sonner";
import { Calculator, Plus, FileDown, RefreshCw, FileText } from "lucide-react";
import { FUENTE_RET_LABEL, fmtMXN, fmtPct, fmtFecha } from "./_common";

export default function RetencionesLISR() {
    const [items, setItems] = useState([]);
    const [calend, setCalend] = useState(null);
    const [open, setOpen] = useState(false);
    const [fids, setFids] = useState([]);
    const [form, setForm] = useState({
        fideicomiso_id: "", anio: new Date().getFullYear(),
        mes: new Date().getMonth() + 1, ingresos_periodo_mxn: 0,
        fuente: "rendimientos_inversion",
        beneficiario_rfc: "", regimen: "actividad_empresarial_art13",
        notas: "",
    });
    const [anioCal, setAnioCal] = useState(new Date().getFullYear());

    const load = async () => {
        const r = await api.get("/fiscal/retenciones");
        setItems(r.data || []);
    };
    const loadCalendario = async (anio = anioCal) => {
        const r = await api.get(`/fiscal/calendario-sat/${anio}`);
        setCalend(r.data);
    };
    const loadFids = async () => {
        try {
            const r = await api.get("/fideicomisos");
            const arr = Array.isArray(r.data) ? r.data : r.data.items || [];
            setFids(arr.map(f => ({ id: f.id, label: f.numero_fideicomiso || f.numero_interno || f.id })));
        } catch { setFids([]); }
    };
    useEffect(() => { load(); loadCalendario(); loadFids(); }, []);

    const calcular = async () => {
        if (!form.fideicomiso_id || !form.beneficiario_rfc) {
            toast.error("Fideicomiso y RFC requeridos"); return;
        }
        try {
            const r = await api.post("/fiscal/retenciones/calcular", form);
            toast.success(`Retención ${fmtMXN(r.data.retencion_calculada_mxn)} (${fmtPct(r.data.tasa_aplicada)})`);
            setOpen(false); load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    const emitirConstancia = async (id) => {
        try {
            const r = await api.post(`/fiscal/retenciones/${id}/emitir-constancia`, {});
            toast.success(`Constancia emitida · ${r.data.constancia_folio_sat}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    const descargar = (id) =>
        window.open(`${api.defaults.baseURL}/fiscal/retenciones/${id}/constancia-pdf`, "_blank");

    return (
        <div className="space-y-6" data-testid="ret-list">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        Retenciones LISR · Art. 13 + Calendario SAT
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Cálculo canónico de retenciones + emisión de constancias.
                        <Badge className="ml-2 bg-amber-100 text-amber-900 border-amber-300 border">MOCK Folio SAT</Badge>
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                            data-testid="ret-nuevo">
                            <Plus className="h-4 w-4 mr-1" /> Calcular retención
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Nueva retención LISR</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs font-semibold">Fideicomiso</label>
                                <Select value={form.fideicomiso_id}
                                    onValueChange={v => setForm({ ...form, fideicomiso_id: v })}>
                                    <SelectTrigger data-testid="ret-sel-fid"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {fids.map(f => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-semibold">Año</label>
                                    <Input type="number" value={form.anio}
                                        onChange={e => setForm({ ...form, anio: Number(e.target.value) })} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Mes</label>
                                    <Input type="number" min="1" max="12" value={form.mes}
                                        onChange={e => setForm({ ...form, mes: Number(e.target.value) })} />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold">Ingresos del periodo MXN</label>
                                <Input type="number" value={form.ingresos_periodo_mxn}
                                    onChange={e => setForm({ ...form, ingresos_periodo_mxn: Number(e.target.value) })} />
                            </div>
                            <div>
                                <label className="text-xs font-semibold">Fuente</label>
                                <Select value={form.fuente}
                                    onValueChange={v => setForm({ ...form, fuente: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(FUENTE_RET_LABEL).map(([k, v]) =>
                                            <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-semibold">RFC beneficiario</label>
                                <Input value={form.beneficiario_rfc}
                                    onChange={e => setForm({ ...form, beneficiario_rfc: e.target.value.toUpperCase() })}
                                    placeholder="XXXX010101AAA" maxLength={13} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                            <Button onClick={calcular} data-testid="ret-calcular-btn">Calcular</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <Tabs defaultValue="ret">
                <TabsList>
                    <TabsTrigger value="ret" data-testid="tab-retenciones">Retenciones</TabsTrigger>
                    <TabsTrigger value="cal" data-testid="tab-calendario">Calendario SAT</TabsTrigger>
                </TabsList>
                <TabsContent value="ret">
                    <Card>
                        <CardContent className="p-0">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/40 text-xs uppercase">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Periodo</th>
                                        <th className="px-3 py-2 text-left">Fuente</th>
                                        <th className="px-3 py-2 text-left">RFC</th>
                                        <th className="px-3 py-2 text-right">Ingresos</th>
                                        <th className="px-3 py-2 text-right">Tasa</th>
                                        <th className="px-3 py-2 text-right">Retención</th>
                                        <th className="px-3 py-2 text-left">Constancia</th>
                                        <th className="px-3 py-2 text-right">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.length === 0 && (
                                        <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">
                                            Sin retenciones. Calcula la primera.
                                        </td></tr>
                                    )}
                                    {items.map(r => (
                                        <tr key={r.id} className="border-t">
                                            <td className="px-3 py-2 text-xs">
                                                {String(r.periodo?.mes).padStart(2, "0")}/{r.periodo?.anio}
                                            </td>
                                            <td className="px-3 py-2 text-xs">{FUENTE_RET_LABEL[r.fuente]}</td>
                                            <td className="px-3 py-2 font-mono text-xs">{r.beneficiario_rfc}</td>
                                            <td className="px-3 py-2 text-right text-xs">{fmtMXN(r.ingresos_periodo_mxn)}</td>
                                            <td className="px-3 py-2 text-right text-xs">{fmtPct(r.tasa_aplicada)}</td>
                                            <td className="px-3 py-2 text-right font-mono text-xs">
                                                {fmtMXN(r.retencion_calculada_mxn)}
                                            </td>
                                            <td className="px-3 py-2 text-xs">
                                                {r.constancia_emitida
                                                    ? <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300 border">
                                                        {r.constancia_folio_sat}
                                                    </Badge>
                                                    : <Badge className="bg-slate-100 text-slate-800 border-slate-200 border">Pendiente</Badge>}
                                            </td>
                                            <td className="px-3 py-2 text-right space-x-1">
                                                {!r.constancia_emitida ? (
                                                    <Button size="sm" variant="outline"
                                                        onClick={() => emitirConstancia(r.id)}
                                                        data-testid={`ret-emitir-${r.id}`}>
                                                        <FileText className="h-3 w-3" />
                                                    </Button>
                                                ) : (
                                                    <Button size="sm" variant="outline"
                                                        onClick={() => descargar(r.id)}
                                                        data-testid={`ret-pdf-${r.id}`}>
                                                        <FileDown className="h-3 w-3" />
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </CardContent>
                    </Card>
                </TabsContent>
                <TabsContent value="cal">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2">
                                Calendario SAT {anioCal}
                                <Input type="number" value={anioCal}
                                    onChange={e => setAnioCal(Number(e.target.value))}
                                    onBlur={() => loadCalendario()}
                                    className="w-24 ml-2" />
                                <Button size="sm" variant="outline" onClick={() => loadCalendario()}>
                                    <RefreshCw className="h-4 w-4" />
                                </Button>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/40 text-xs uppercase">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Código</th>
                                        <th className="px-3 py-2 text-left">Obligación</th>
                                        <th className="px-3 py-2 text-left">Periodo</th>
                                        <th className="px-3 py-2 text-left">Fecha límite</th>
                                        <th className="px-3 py-2 text-left">Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(calend?.obligaciones || []).map((o, i) => (
                                        <tr key={i} className="border-t">
                                            <td className="px-3 py-2 font-mono text-xs">{o.codigo}</td>
                                            <td className="px-3 py-2 text-xs">{o.nombre}</td>
                                            <td className="px-3 py-2 text-xs">{o.periodo}</td>
                                            <td className="px-3 py-2 text-xs">{fmtFecha(o.fecha_limite)}</td>
                                            <td className="px-3 py-2 text-xs">
                                                {o.pasada
                                                    ? <Badge className="bg-slate-200 text-slate-700 border-slate-300 border">pasada</Badge>
                                                    : <Badge className="bg-sky-100 text-sky-900 border-sky-300 border">próxima</Badge>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
